import React, { useState, useEffect } from 'react';
import { userApi, campusApi, managementApi } from '../services/api';
import { PageHead, Badge, Btn, TableWrap, Th, Td, FormGroup, Input, Select, Textarea, Spinner, Toast, Modal, Empty, Card } from '../components/UI';

const ROLES = [{ id:1, name:'Admin' },{ id:2, name:'Teacher' },{ id:3, name:'Management' },{ id:4, name:'Viewer' }];

export default function UsersPage() {
  const [users,    setUsers]    = useState([]);
  const [campuses, setCampuses] = useState([]);
  const [campusId, setCampusId] = useState('');
  const [roleId,   setRoleId]   = useState('');
  const [status,   setStatus]   = useState('');
  const [search,   setSearch]   = useState('');
  const [loading,  setLoading]  = useState(true);
  const [toast,    setToast]    = useState(null);
  const [modal,    setModal]    = useState(null); // null | 'create' | 'edit' | 'lock' | 'devices'
  const [selUser,  setSelUser]  = useState(null);
  const [lockReason, setLockReason] = useState('');
  const [devices,  setDevices]  = useState([]);
  const [saving,   setSaving]   = useState(false);
  const [editForm, setEditForm] = useState({ fullName:'', campusID:'', roleID:2 });
  const [createForm, setCreateForm] = useState({ fullName:'', email:'', campusID:'', roleID:2 });

  useEffect(() => {
    campusApi.getAll()
      .then(r => setCampuses(r.data.data || []))
      .catch(() => setCampuses([{ campusID:1, campusName:'Chinchwad' },{ campusID:2, campusName:'Hinjawadi' }]));
  }, []);

  useEffect(() => { load(); }, [campusId, roleId]);

  async function load() {
    setLoading(true);
    try {
      const res = await userApi.getAll(campusId || undefined, roleId || undefined);
      setUsers(res.data.data || []);
    } catch { setUsers(DEMO_USERS); }
    finally { setLoading(false); }
  }

  async function handleLock(u) {
    if (!lockReason.trim()) return;
    setSaving(true);
    try {
      const res = await userApi.lock(u.userID, lockReason);
      if (res.data?.success) {
        setToast({ msg: res.data.message || 'User locked successfully.', type:'success' });
        setModal(null);
        load();
      } else {
        setToast({ msg: res.data?.message || 'Failed to lock user.', type:'error' });
      }
    } catch (err) { setToast({ msg: err.response?.data?.message || 'Failed.', type:'error' }); }
    finally { setSaving(false); }
  }

  async function handleUnlock(u) {
    try {
      const res = await userApi.unlock(u.userID);
      if (res.data?.success) {
        setToast({ msg: res.data.message || 'User unlocked.', type:'success' });
        load();
      } else {
        setToast({ msg: res.data?.message || 'Failed to unlock user.', type:'error' });
      }
    } catch (err) { setToast({ msg: err.response?.data?.message || 'Failed to unlock.', type:'error' }); }
  }

  async function handleApprove(u) {
    try {
      const res = await userApi.approve(u.userID);
      if (res.data?.success) {
        setToast({ msg: res.data.message || 'User approved.', type:'success' });
        load();
      } else {
        setToast({ msg: res.data?.message || 'Failed to approve user.', type:'error' });
      }
    } catch (err) { setToast({ msg: err.response?.data?.message || 'Failed to approve user.', type:'error' }); }
  }

  async function handleEditSave(e) {
    e.preventDefault(); setSaving(true);
    try {
      await userApi.update(selUser.userID, { fullName: editForm.fullName, campusID: parseInt(editForm.campusID) || null, roleID: parseInt(editForm.roleID) });
      setToast({ msg:'User updated.', type:'success' }); setModal(null); load();
    } catch (err) { setToast({ msg: err.response?.data?.message || 'Failed.', type:'error' }); }
    finally { setSaving(false); }
  }

  async function handleCreateSave(e) {
    e.preventDefault(); setSaving(true);
    try {
      await userApi.create({
        fullName: createForm.fullName,
        email: createForm.email,
        campusID: parseInt(createForm.campusID) || null,
        roleID: parseInt(createForm.roleID),
      });
      setToast({ msg:'User created.', type:'success' });
      setCreateForm({ fullName:'', email:'', campusID:'', roleID:2 });
      setModal(null);
      load();
    } catch (err) { setToast({ msg: err.response?.data?.message || 'Failed to create user.', type:'error' }); }
    finally { setSaving(false); }
  }

  async function openDevices(u) {
    setSelUser(u); setModal('devices');
    try { const r = await userApi.getDevices(u.userID); setDevices(r.data.data || []); }
    catch { setDevices([]); }
  }

  async function revokeDevice(userId, deviceId) {
    try {
      await userApi.revokeDevice(userId, deviceId);
      setToast({ msg:'Device revoked.', type:'success' });
      openDevices(selUser);
    } catch { setToast({ msg:'Failed.', type:'error' }); }
  }

  const displayed = users.filter(u => {
    const q = search.toLowerCase();
    const matchesSearch = !q || u.fullName?.toLowerCase().includes(q) || u.email?.toLowerCase().includes(q);
    const effectiveStatus = u.isLocked ? 'locked' : (u.isActive ? 'active' : 'pending');
    const matchesStatus = !status || effectiveStatus === status;
    return matchesSearch && matchesStatus;
  });

  return (
    <div className="page-anim">
      <PageHead
        title="Users"
        subtitle="Manage all system users"
        actions={
          <Btn
            variant="primary"
            onClick={() => {
              setCreateForm({ fullName:'', email:'', campusID:'', roleID:2 });
              setModal('create');
            }}
          >
            + Create User
          </Btn>
        }
      />

      {/* Filters */}
      <Card style={{ marginBottom:'1rem', padding:'1rem 1.2rem' }}>
        <div style={{ display:'flex', gap:8, flexWrap:'wrap', alignItems:'center' }}>
          <select value={campusId} onChange={e => setCampusId(e.target.value)}
            style={{ padding:'7px 12px', fontSize:13, border:'1.5px solid var(--border)', borderRadius:8, fontFamily:'var(--font-body)', outline:'none', background:'white' }}>
            <option value="">All Campuses</option>
            {campuses.map(c => <option key={c.campusID} value={c.campusID}>{c.campusName}</option>)}
          </select>
          <select value={roleId} onChange={e => setRoleId(e.target.value)}
            style={{ padding:'7px 12px', fontSize:13, border:'1.5px solid var(--border)', borderRadius:8, fontFamily:'var(--font-body)', outline:'none', background:'white' }}>
            <option value="">All Roles</option>
            {ROLES.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
          </select>
          <select value={status} onChange={e => setStatus(e.target.value)}
            style={{ padding:'7px 12px', fontSize:13, border:'1.5px solid var(--border)', borderRadius:8, fontFamily:'var(--font-body)', outline:'none', background:'white' }}>
            <option value="">All Status</option>
            <option value="active">Active</option>
            <option value="pending">Pending Approval</option>
            <option value="locked">Locked</option>
          </select>
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="🔍 Search name or email…"
            style={{ padding:'7px 12px', fontSize:13, flex:1, minWidth:160, border:'1.5px solid var(--border)', borderRadius:8, outline:'none', fontFamily:'var(--font-body)' }} />
        </div>
      </Card>

      {loading ? (
        <div style={{ display:'flex', justifyContent:'center', padding:'3rem', gap:10 }}><Spinner size={24}/><span style={{ color:'var(--text2)' }}>Loading…</span></div>
      ) : displayed.length === 0 ? <Empty icon="👥" text="No users found" /> : (
        <TableWrap>
          <thead><tr>
            <Th>User</Th><Th>Email</Th><Th>Campus</Th><Th>Role</Th><Th>Status</Th><Th>Actions</Th>
          </tr></thead>
          <tbody>
            {displayed.map(u => (
              <tr key={u.userID}>
                <Td>
                  <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                    <div style={{ width:32, height:32, borderRadius:'50%', background:'linear-gradient(135deg,var(--blue),var(--green))', color:'white', fontSize:12, fontWeight:700, display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                      {u.fullName?.split(' ').map(w=>w[0]).join('').slice(0,2).toUpperCase()}
                    </div>
                    <div style={{ fontWeight:600 }}>{u.fullName}</div>
                  </div>
                </Td>
                <Td style={{ color:'var(--text2)', fontSize:12 }}>{u.email}</Td>
                <Td>{u.campusName || '—'}</Td>
                <Td>
                  <span style={{ background:'var(--blue-light)', color:'var(--blue-dark)', fontSize:11, fontWeight:700, padding:'3px 9px', borderRadius:20 }}>{u.roleName}</span>
                </Td>
                <Td>
                  <span style={{ background: u.isLocked ? 'var(--red-light)' : u.isActive ? 'var(--green-light)' : 'var(--bg2)', color: u.isLocked ? 'var(--red)' : u.isActive ? 'var(--green)' : 'var(--text3)', fontSize:11, fontWeight:700, padding:'3px 9px', borderRadius:20 }}>
                    {u.isLocked ? 'Locked' : u.isActive ? 'Active' : 'Pending Approval'}
                  </span>
                </Td>
                <Td>
                  <div style={{ display:'flex', gap:5, flexWrap:'wrap' }}>
                    <Btn size="sm" variant="ghost" onClick={() => { setSelUser(u); setEditForm({ fullName: u.fullName, campusID: u.campusID||'', roleID: u.roleID }); setModal('edit'); }}>✏️</Btn>
                    {!u.isActive && (
                      <Btn size="sm" variant="green" onClick={() => handleApprove(u)}>Approve</Btn>
                    )}
                    {u.isLocked
                      ? <Btn size="sm" variant="green" onClick={() => handleUnlock(u)}>🔓</Btn>
                      : <Btn size="sm" variant="red"   onClick={() => { setSelUser(u); setLockReason(''); setModal('lock'); }}>🔒</Btn>
                    }
                    <Btn size="sm" variant="ghost" onClick={() => openDevices(u)}>📱</Btn>
                  </div>
                </Td>
              </tr>
            ))}
          </tbody>
        </TableWrap>
      )}

      {/* Create Modal */}
      {modal === 'create' && (
        <Modal title="Create User" onClose={() => setModal(null)}>
          <form onSubmit={handleCreateSave}>
            <div style={{ display:'flex', flexDirection:'column', gap:'1rem', marginBottom:'1rem' }}>
              <FormGroup label="Full Name *">
                <Input value={createForm.fullName} onChange={e => setCreateForm(f => ({ ...f, fullName: e.target.value }))} required />
              </FormGroup>
              <FormGroup label="Email *">
                <Input type="email" value={createForm.email} onChange={e => setCreateForm(f => ({ ...f, email: e.target.value }))} required />
              </FormGroup>
              <FormGroup label="Campus">
                <Select value={createForm.campusID} onChange={e => setCreateForm(f => ({ ...f, campusID: e.target.value }))}>
                  <option value="">-- None (Admin) --</option>
                  {campuses.map(c => <option key={c.campusID} value={c.campusID}>{c.campusName}</option>)}
                </Select>
              </FormGroup>
              <FormGroup label="Role *">
                <Select value={createForm.roleID} onChange={e => setCreateForm(f => ({ ...f, roleID: e.target.value }))}>
                  {ROLES.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
                </Select>
              </FormGroup>
            </div>
            <div style={{ display:'flex', gap:10 }}>
              <Btn type="submit" variant="primary" disabled={saving} style={{ flex:1, justifyContent:'center' }}>
                {saving ? <Spinner size={16} color="white"/> : 'Create User'}
              </Btn>
              <Btn variant="ghost" onClick={() => setModal(null)}>Cancel</Btn>
            </div>
          </form>
        </Modal>
      )}

      {/* Edit Modal */}
      {modal === 'edit' && selUser && (
        <Modal title="Edit User" onClose={() => setModal(null)}>
          <form onSubmit={handleEditSave}>
            <div style={{ display:'flex', flexDirection:'column', gap:'1rem', marginBottom:'1rem' }}>
              <FormGroup label="Full Name *">
                <Input value={editForm.fullName} onChange={e => setEditForm(f => ({ ...f, fullName: e.target.value }))} required />
              </FormGroup>
              <FormGroup label="Campus">
                <Select value={editForm.campusID} onChange={e => setEditForm(f => ({ ...f, campusID: e.target.value }))}>
                  <option value="">-- None (Admin) --</option>
                  {campuses.map(c => <option key={c.campusID} value={c.campusID}>{c.campusName}</option>)}
                </Select>
              </FormGroup>
              <FormGroup label="Role *">
                <Select value={editForm.roleID} onChange={e => setEditForm(f => ({ ...f, roleID: e.target.value }))}>
                  {ROLES.map(r => <option key={r.id} value={r.id}>{r.name}</option>)}
                </Select>
              </FormGroup>
            </div>
            <div style={{ display:'flex', gap:10 }}>
              <Btn type="submit" variant="primary" disabled={saving} style={{ flex:1, justifyContent:'center' }}>
                {saving ? <Spinner size={16} color="white"/> : '💾 Save Changes'}
              </Btn>
              <Btn variant="ghost" onClick={() => setModal(null)}>Cancel</Btn>
            </div>
          </form>
        </Modal>
      )}

      {/* Lock Modal */}
      {modal === 'lock' && selUser && (
        <Modal title="Lock User Account" onClose={() => setModal(null)}>
          <div style={{ padding:'12px 14px', background:'var(--red-light)', borderRadius:8, marginBottom:'1rem' }}>
            <div style={{ fontWeight:700 }}>{selUser.fullName}</div>
            <div style={{ fontSize:12, color:'var(--text2)' }}>{selUser.email}</div>
          </div>
          <FormGroup label="Reason for Locking *">
            <Textarea value={lockReason} onChange={e => setLockReason(e.target.value)} placeholder="Reason..." speechToText style={{ minHeight: 130 }} />
          </FormGroup>
          <div style={{ display:'flex', gap:10, marginTop:'1rem' }}>
            <Btn variant="red" onClick={() => handleLock(selUser)} disabled={saving || !lockReason.trim()} style={{ flex:1, justifyContent:'center' }}>
              {saving ? <Spinner size={16} color="var(--red)"/> : '🔒 Lock Account'}
            </Btn>
            <Btn variant="ghost" onClick={() => setModal(null)}>Cancel</Btn>
          </div>
        </Modal>
      )}

      {/* Devices Modal */}
      {modal === 'devices' && selUser && (
        <Modal title={`Trusted Devices – ${selUser.fullName}`} onClose={() => setModal(null)}>
          {devices.length === 0 ? <Empty icon="📱" text="No trusted devices" /> : devices.map(d => (
            <div key={d.deviceID} style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'10px 14px', border:'1.5px solid var(--border)', borderRadius:8, marginBottom:8 }}>
              <div>
                <div style={{ fontWeight:600, fontSize:13 }}>{d.deviceName || 'Unknown Device'}</div>
                <div style={{ fontSize:11, color:'var(--text2)' }}>Expires: {d.expiresAt?.slice(0,10)} · IP: {d.iPAddress || '—'}</div>
              </div>
              <Btn size="sm" variant="red" onClick={() => revokeDevice(selUser.userID, d.deviceID)}>Revoke</Btn>
            </div>
          ))}
          <Btn variant="ghost" onClick={() => setModal(null)} style={{ marginTop:8 }}>Close</Btn>
        </Modal>
      )}

      {toast && <Toast msg={toast.msg} type={toast.type} onClose={() => setToast(null)} />}
    </div>
  );
}

const DEMO_USERS = [
  { userID:1, fullName:'System Admin',  email:'admin@elproschools.edu.in',   campusID:null, campusName:'—',         roleName:'Admin',      roleID:1, isActive:true,  isLocked:false },
  { userID:2, fullName:'Priya Sharma',  email:'priya@elproschools.edu.in',   campusID:1,   campusName:'Chinchwad', roleName:'Teacher',    roleID:2, isActive:true,  isLocked:false },
  { userID:3, fullName:'Rahul Mehta',   email:'rahul@elproschools.edu.in',   campusID:1,   campusName:'Chinchwad', roleName:'Teacher',    roleID:2, isActive:true,  isLocked:false },
  { userID:4, fullName:'Anjali Patel',  email:'anjali@elproschools.edu.in',  campusID:2,   campusName:'Hinjawadi', roleName:'Management', roleID:3, isActive:true,  isLocked:false },
  { userID:5, fullName:'Suresh Kumar',  email:'suresh@elproschools.edu.in',  campusID:1,   campusName:'Chinchwad', roleName:'Management', roleID:3, isActive:true,  isLocked:false },
  { userID:6, fullName:'Meena Singh',   email:'meena@elproschools.edu.in',   campusID:1,   campusName:'Chinchwad', roleName:'Teacher',    roleID:2, isActive:true,  isLocked:true  },
];




